// src/dnd/emitter_tool/components/panels/RotationPanel.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";

/** Persisted open/closed state key */
const OPEN_KEY = "EmitterTool:RotationPanelOpenV1";

/** Simple collapsible wrapper with persisted state (no overflow) */
function CollapsibleBox({ title, defaultOpen = true, children }) {
  const [open, setOpen] = useState(() => {
    try {
      const saved = localStorage.getItem(OPEN_KEY);
      return saved == null ? defaultOpen : saved === "1";
    } catch {
      return defaultOpen;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(OPEN_KEY, open ? "1" : "0");
    } catch {}
  }, [open]);

  return (
    <div
      style={{
        border: "1px solid #334155",
        borderRadius: 8,
        marginBottom: 12,
        background: "#0b0f17",
        maxWidth: "100%",
        boxSizing: "border-box",
        overflowX: "hidden",
      }}
    >
      <div
        onClick={() => setOpen((v) => !v)}
        style={{
          padding: "8px 10px",
          background: "#18212f",
          userSelect: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontWeight: 600,
          gap: 8,
        }}
      >
        <span
          style={{
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {title}
        </span>
        <span style={{ opacity: 0.75, flex: "0 0 auto" }}>
          {open ? "▾" : "▸"}
        </span>
      </div>
      {open && <div style={{ padding: 10 }}>{children}</div>}
    </div>
  );
}

const labelStyle = { fontSize: 12, opacity: 0.9, overflowWrap: "anywhere" };

function clamp(val, min, max) {
  return Math.max(min, Math.min(max, val));
}
function toNum(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

/** Vertical field: label → number → range */
function StackedField({
  label,
  value,
  onChange,
  min = 0,
  max = 10,
  step = 0.1,
  suffix = "",
}) {
  const [text, setText] = useState(
    Number.isFinite(value) ? String(value) : String(min)
  );

  useEffect(() => {
    const asStr = String(Number.isFinite(value) ? value : min);
    if (asStr !== text) setText(asStr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, min, max]);

  const commitNumber = (raw) => {
    const parsed = toNum(raw, value ?? min);
    const clamped = clamp(parsed, min, max);
    onChange?.(clamped);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0, marginBottom: 10 }}>
      <div style={{ ...labelStyle }}>{label}</div>

      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <input
          type="number"
          value={text}
          min={min}
          max={max}
          step={step}
          onChange={(e) => setText(e.target.value)}
          onBlur={(e) => commitNumber(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
          style={{
            width: "100%",
            background: "#0b0d12",
            color: "white",
            border: "1px solid #374151",
            borderRadius: 6,
            padding: "6px 8px",
            fontSize: 12,
            textAlign: "right",
            boxSizing: "border-box",
          }}
        />
        {suffix ? (
          <span style={{ opacity: 0.7, whiteSpace: "nowrap", fontSize: 12 }}>
            {suffix}
          </span>
        ) : null}
      </div>

      <input
        type="range"
        value={Number.isFinite(value) ? value : min}
        min={min}
        max={max}
        step={step}
        onChange={(e) => {
          const v = toNum(e.target.value, min);
          onChange?.(v);
        }}
        style={{
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
          accentColor: "#64748b",
          cursor: "pointer",
        }}
      />
    </div>
  );
}

/** Small segmented buttons */
function Segmented({ value, onChange, options }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${options.length}, 1fr)`,
        background: "#0b0d12",
        border: "1px solid #374151",
        borderRadius: 8,
        overflow: "hidden",
      }}
    >
      {options.map((opt, i) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value ?? i}
            onClick={() => onChange?.(opt.value)}
            title={opt.title || ""}
            style={{
              padding: "6px 8px",
              fontSize: 12,
              color: active ? "#e5e7eb" : "#94a3b8",
              background: active ? "#1f2937" : "transparent",
              border: "none",
              borderRight: i < options.length - 1 ? "1px solid #374151" : "none",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Simple particle rotation panel
 * Keys used:
 *  - rotateEnabled: boolean
 *  - rotateDirection: "cw" | "ccw"
 *  - rotateSpeedAbsDps: number
 *  - rotateSpeedDegPerS: number (derived signed)
 */
export default function RotationPanel({
  effectSettings = {},
  setEffectSettings,
  title = "Rotation",
  defaultOpen = false,
}) {
  const setES = (patch) => {
    if (typeof setEffectSettings === "function") {
      setEffectSettings((cur) => {
        const base = cur || {};
        const next = typeof patch === "function" ? patch(base) : patch;
        return { ...base, ...next };
      });
    }
  };

  const {
    rotateEnabled = false,
    rotateDirection = "ccw",
    rotateSpeedAbsDps = 90,
    rotateSpeedDegPerS = (rotateDirection === "ccw" ? 1 : -1) * (rotateSpeedAbsDps ?? 0),
  } = effectSettings || {};

  // === Derive the canonical "patch" we want to broadcast ===
  const canonical = useMemo(() => {
    const signed = Number.isFinite(rotateSpeedDegPerS)
      ? rotateSpeedDegPerS
      : (rotateDirection === "ccw" ? 1 : -1) * (rotateSpeedAbsDps ?? 0);

    return {
      rotateEnabled: !!rotateEnabled,
      rotateDirection: rotateDirection === "cw" ? "cw" : "ccw",
      rotateSpeedAbsDps: Math.max(0, Number(rotateSpeedAbsDps || 0)),
      rotateSpeedDegPerS: Number.isFinite(signed) ? signed : 0,
    };
  }, [rotateEnabled, rotateDirection, rotateSpeedAbsDps, rotateSpeedDegPerS]);

  // Keep a ref of last broadcast to avoid spamming identical events
  const lastBroadcastRef = useRef(null);
  const broadcast = (patch) => {
    const prev = lastBroadcastRef.current || {};
    const same =
      prev.rotateEnabled === patch.rotateEnabled &&
      prev.rotateDirection === patch.rotateDirection &&
      prev.rotateSpeedAbsDps === patch.rotateSpeedAbsDps &&
      prev.rotateSpeedDegPerS === patch.rotateSpeedDegPerS;

    if (same) return;
    lastBroadcastRef.current = patch;
    try {
      window.dispatchEvent(
        new CustomEvent("EmitterTool:ParticlesConfig", { detail: { ...patch } })
      );
      // Optional trace:
      // console.debug("[RotationPanel] Emitted ParticlesConfig", patch);
    } catch {}
  };

  // Emit on mount and whenever canonical changes
  useEffect(() => {
    broadcast(canonical);
    // sync local effectSettings store too (keeps UI state consistent)
    setES(canonical);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canonical.rotateEnabled, canonical.rotateDirection, canonical.rotateSpeedAbsDps, canonical.rotateSpeedDegPerS]);

  // === UI Handlers ===
  const updateEnabled = (checked) => {
    const patch = { ...canonical, rotateEnabled: !!checked };
    setES(patch);
    broadcast(patch);
  };
  const updateDirection = (dir) => {
    const patch = { ...canonical, rotateDirection: dir === "cw" ? "cw" : "ccw" };
    // recompute signed from abs + dir
    const signed = (patch.rotateDirection === "ccw" ? 1 : -1) * (patch.rotateSpeedAbsDps ?? 0);
    patch.rotateSpeedDegPerS = signed;
    setES(patch);
    broadcast(patch);
  };
  const updateAbsSpeed = (abs) => {
    const v = Math.max(0, Number(abs || 0));
    const patch = { ...canonical, rotateSpeedAbsDps: v };
    const signed = (patch.rotateDirection === "ccw" ? 1 : -1) * v;
    patch.rotateSpeedDegPerS = signed;
    setES(patch);
    broadcast(patch);
  };

  return (
    <CollapsibleBox title={title} defaultOpen={defaultOpen}>
      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 10,
        }}
      >
        <input
          type="checkbox"
          checked={!!canonical.rotateEnabled}
          onChange={(e) => updateEnabled(e.target.checked)}
          style={{ accentColor: "#64748b" }}
        />
        <span style={{ fontSize: 12 }}>Enable rotation</span>
      </label>

      <div style={{ ...labelStyle, marginBottom: 6 }}>Direction</div>
      <Segmented
        value={canonical.rotateDirection}
        onChange={(v) => updateDirection(v)}
        options={[
          { value: "ccw", label: "Counter-Clockwise" },
          { value: "cw",  label: "Clockwise" },
        ]}
      />

      <StackedField
        label="Angular speed"
        value={canonical.rotateSpeedAbsDps}
        onChange={(v) => updateAbsSpeed(v)}
        min={0}
        max={1440}
        step={1}
        suffix="°/s"
      />
    </CollapsibleBox>
  );
}

export { RotationPanel };
